// Route Configs API Service
// Handles CRUD operations for user-owned route configurations

import { apiFetch } from "./apiFetch";
import type { GenerationType } from "./provider-templates";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface Credentials {
	apiKey?: string;
	baseUrl?: string;
	orgId?: string;
}

export interface ModelParameters {
	temperature?: number;
	maxTokens?: number;
	topP?: number;
	topK?: number;
	repeatPenalty?: number;
	contextWindow?: number;
	stopSequences?: string[];
}

export interface RouteConfigModelConfig {
	id: string;
	modelConfigId: string;
	modelConfig: {
		id: string;
		name: string;
		description: string | null;
		generationType: string;
		providerType: string;
		modelIdentifier: string;
		modelDisplayName: string | null;
		isHosted: boolean;
		connectionStatus: string;
		connectionError: string | null;
		lastPingAt: string | null;
		isActive: boolean;
	};
	isPrimary: boolean;
	priority: number;
}

export interface RouteConfig {
	id: string;
	userId: string;
	name: string;
	description: string | null;
	generationType: string;
	parameters: ModelParameters | null;
	systemPrompt: string | null;
	prePromptItems: string[] | null;
	embeddingModelConfig: {
		id: string;
		name: string;
		modelIdentifier: string;
		providerType: string;
	} | null;
	hotCacheEnabled: boolean;
	semanticCacheEnabled: boolean;
	customCacheTtl: number | null;
	ragEnabled: boolean;
	// Model configs (linked via RouteConfigModelConfig)
	models: RouteConfigModelConfig[];
	// Routing & Traffic Rules (Between Configs)
	isActive: boolean;
	// NSFW Compliance
	isNsfw: boolean;
	nsfwLevel: string | null;
	nsfwWarning: string | null;
	requiresAgeVerification: boolean;
	// Response Format
	responseFormat: any | null;
	createdAt: string;
	updatedAt: string;
}

export interface RouteConfigStats {
	totalRequests: number;
	hotHits: number;
	semanticHits: number;
	misses: number;
	cacheHitRate: number;
	totalTokens: number;
	savedTokens: number;
	savedAmount: number;
	avgResponseTime: number;
}

export interface RouteConfigWithStats extends RouteConfig {
	stats: RouteConfigStats;
}

export interface ProjectAccess {
	id: string;
	projectId: string;
	projectName: string;
	isDefault: boolean;
	priority: number;
	isEnabled: boolean;
	createdAt: string;
}

export interface CreateRouteConfigData {
	name: string;
	description?: string;
	generationType?: GenerationType;
	parameters?: ModelParameters;
	systemPrompt?: string;
	prePromptItems?: string[];
	embeddingModelConfigId?: string;
	hotCacheEnabled?: boolean;
	semanticCacheEnabled?: boolean;
	customCacheTtl?: number;
	ragEnabled?: boolean;
	isActive?: boolean;
	// Routing fields (Between Configs)
	// NSFW Compliance
	isNsfw?: boolean;
	nsfwLevel?: "mild" | "moderate" | "explicit";
	nsfwWarning?: string;
	requiresAgeVerification?: boolean;
	// Response Format
	responseFormat?: any;
}

export interface UpdateRouteConfigData
	extends Partial<
		Omit<
			CreateRouteConfigData,
			| "description"
			| "credentials"
			| "systemPrompt"
			| "prePromptItems"
			| "customCacheTtl"
		>
	> {
	description?: string | null | undefined;
	credentials?: Credentials | null | undefined;
	systemPrompt?: string | null | undefined;
	prePromptItems?: string[] | null | undefined;
	customCacheTtl?: number | null | undefined;
}

export interface GrantProjectAccessData {
	projectId: string;
	isDefault?: boolean;
	priority?: number;
}

export interface TestConnectionResult {
	success: boolean;
	responseTime: number;
	response?: string;
	error?: string;
	models?: Array<{ id: string; name: string }>;
}

export interface ProviderModel {
	id: string;
	name: string;
	description?: string;
	contextWindow?: number;
	capabilities?: {
		vision?: boolean;
		functionCalling?: boolean;
		streaming?: boolean;
	};
}

export interface PaginationMeta {
	page: number;
	limit: number;
	total: number;
	totalPages: number;
	hasNext: boolean;
	hasPrev: boolean;
}

export interface PaginatedResponse<T> {
	data: T[];
	meta: PaginationMeta;
}

// ═══════════════════════════════════════════════════════════════════════════
// API FUNCTIONS - USER'S ROUTE CONFIGS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create a new route config (owned by current user)
 */
export async function createRouteConfig(
	data: CreateRouteConfigData,
): Promise<RouteConfig> {
	return apiFetch<RouteConfig>("/route-configs", {
		method: "POST",
		body: JSON.stringify(data),
	});
}

/**
 * List all route configs owned by the current user
 */
export async function listMyRouteConfigs(options?: {
	page?: number;
	limit?: number;
	search?: string;
	providerType?: string;
	generationType?: GenerationType;
	isActive?: boolean;
}): Promise<PaginatedResponse<RouteConfig>> {
	const params = new URLSearchParams();
	if (options?.page) params.set("page", String(options.page));
	if (options?.limit) params.set("limit", String(options.limit));
	if (options?.search) params.set("search", options.search);
	if (options?.providerType) params.set("providerType", options.providerType);
	if (options?.generationType)
		params.set("generationType", options.generationType);
	if (options?.isActive !== undefined)
		params.set("isActive", String(options.isActive));

	const query = params.toString();
	// apiFetch unwraps { data: ... } so we receive the array directly
	// We need to wrap it back into a PaginatedResponse
	const result = await apiFetch<RouteConfig[] | PaginatedResponse<RouteConfig>>(
		`/route-configs${query ? `?${query}` : ""}`,
	);

	// Transform backend response format to frontend format
	// Backend returns: { modelId, model }
	// Frontend expects: { modelConfigId, modelConfig }
	const transformRouteConfig = (config: any): RouteConfig => {
		if (config.models && Array.isArray(config.models)) {
			config.models = config.models.map((m: any) => ({
				id: m.id,
				modelConfigId: m.modelId || m.modelConfigId, // Support both formats
				modelConfig: m.model || m.modelConfig, // Support both formats
				isPrimary: m.isPrimary,
				priority: m.priority,
			}));
		}
		return config as RouteConfig;
	};

	// Handle both array (unwrapped) and full response formats
	if (Array.isArray(result)) {
		return {
			data: result.map(transformRouteConfig),
			meta: {
				page: options?.page || 1,
				limit: options?.limit || 50,
				total: result.length,
				totalPages: 1,
				hasNext: false,
				hasPrev: false,
			},
		};
	}
	return {
		...result,
		data: result.data.map(transformRouteConfig),
	};
}

/**
 * Get a route config by ID
 */
export async function getRouteConfig(configId: string): Promise<RouteConfig> {
	const response = await apiFetch<any>(`/route-configs/${configId}`);

	// Transform backend response format to frontend format
	// Backend returns: { modelId, model }
	// Frontend expects: { modelConfigId, modelConfig }
	if (response.models && Array.isArray(response.models)) {
		response.models = response.models.map((m: any) => ({
			id: m.id,
			modelConfigId: m.modelId || m.modelConfigId, // Support both formats
			modelConfig: m.model || m.modelConfig, // Support both formats
			isPrimary: m.isPrimary,
			priority: m.priority,
		}));
	}

	return response as RouteConfig;
}

/**
 * Get model configs linked to a route config
 */
export async function getRouteConfigModelConfigs(
	configId: string,
): Promise<RouteConfigModelConfig[]> {
	return apiFetch<RouteConfigModelConfig[]>(
		`/route-configs/${configId}/model-configs`,
	);
}

/**
 * Get a route config with usage stats
 */
export async function getRouteConfigWithStats(
	configId: string,
): Promise<RouteConfigWithStats> {
	return apiFetch<RouteConfigWithStats>(`/route-configs/${configId}/stats`);
}

/**
 * Update a route config
 */
export async function updateRouteConfig(
	configId: string,
	data: UpdateRouteConfigData,
): Promise<RouteConfig> {
	return apiFetch<RouteConfig>(`/route-configs/${configId}`, {
		method: "PUT",
		body: JSON.stringify(data),
	});
}

/**
 * Delete a route config
 */
export async function deleteRouteConfig(configId: string): Promise<void> {
	await apiFetch<{ message: string }>(`/route-configs/${configId}`, {
		method: "DELETE",
	});
}

/**
 * Test route config connection
 */
export async function testRouteConfigConnection(
	configId: string,
	prompt?: string,
): Promise<TestConnectionResult> {
	return apiFetch<TestConnectionResult>(`/route-configs/${configId}/test`, {
		method: "POST",
		body: JSON.stringify({
			prompt: prompt || "Hello, this is a test message.",
		}),
	});
}

// ═══════════════════════════════════════════════════════════════════════════
// API FUNCTIONS - MODEL CONFIG MANAGEMENT IN ROUTE CONFIGS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Add a model config to a route config
 */
export async function addModelConfigToRouteConfig(
	routeConfigId: string,
	data: { modelConfigId: string; isPrimary?: boolean; priority?: number },
): Promise<void> {
	await apiFetch(`/route-configs/${routeConfigId}/model-configs`, {
		method: "POST",
		body: JSON.stringify(data),
	});
}

/**
 * Remove a model config from a route config
 */
export async function removeModelConfigFromRouteConfig(
	routeConfigId: string,
	modelConfigId: string,
): Promise<void> {
	await apiFetch(
		`/route-configs/${routeConfigId}/model-configs/${modelConfigId}`,
		{
			method: "DELETE",
		},
	);
}

/**
 * Set primary model config for a route config
 */
export async function setPrimaryModelConfig(
	routeConfigId: string,
	modelConfigId: string,
): Promise<void> {
	await apiFetch(`/route-configs/${routeConfigId}/model-configs/primary`, {
		method: "PUT",
		body: JSON.stringify({ modelConfigId }),
	});
}

/**
 * Update model config priority in a route config
 */
export async function updateModelConfigPriority(
	routeConfigId: string,
	modelConfigId: string,
	priority: number,
): Promise<void> {
	await apiFetch(
		`/route-configs/${routeConfigId}/model-configs/${modelConfigId}/priority`,
		{
			method: "PUT",
			body: JSON.stringify({ priority }),
		},
	);
}

// ═══════════════════════════════════════════════════════════════════════════
// API FUNCTIONS - PROJECT ACCESS MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Grant a project access to a route config
 */
export async function grantProjectAccess(
	configId: string,
	data: GrantProjectAccessData,
): Promise<{ success: boolean; accessId: string }> {
	return apiFetch<{ success: boolean; accessId: string }>(
		`/route-configs/${configId}/access`,
		{
			method: "POST",
			body: JSON.stringify(data),
		},
	);
}

/**
 * Revoke a project's access to a route config
 */
export async function revokeProjectAccess(
	configId: string,
	projectId: string,
): Promise<void> {
	await apiFetch<{ message: string }>(
		`/route-configs/${configId}/access/${projectId}`,
		{
			method: "DELETE",
		},
	);
}

/**
 * List projects with access to a route config
 */
export async function listProjectAccess(
	configId: string,
): Promise<ProjectAccess[]> {
	return apiFetch<ProjectAccess[]>(`/route-configs/${configId}/access`);
}

// ═══════════════════════════════════════════════════════════════════════════
// API FUNCTIONS - PROJECT-SCOPED CONFIGS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * List route configs accessible by a project
 */
export async function listProjectRouteConfigs(
	projectId: string,
	options?: {
		page?: number;
		limit?: number;
	},
): Promise<PaginatedResponse<RouteConfig>> {
	const params = new URLSearchParams();
	if (options?.page) params.set("page", String(options.page));
	if (options?.limit) params.set("limit", String(options.limit));

	const query = params.toString();
	// apiFetch unwraps { data: ... } so we receive the array directly
	const result = await apiFetch<RouteConfig[] | PaginatedResponse<RouteConfig>>(
		`/projects/${projectId}/route-configs${query ? `?${query}` : ""}`,
	);

	// Handle both array (unwrapped) and full response formats
	if (Array.isArray(result)) {
		return {
			data: result,
			meta: {
				page: options?.page || 1,
				limit: options?.limit || 100,
				total: result.length,
				totalPages: 1,
				hasNext: false,
				hasPrev: false,
			},
		};
	}
	return result;
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get connection status display info
 */
export function getConnectionStatusInfo(
	status: RouteConfigModelConfig["modelConfig"]["connectionStatus"],
): {
	label: string;
	color: "green" | "yellow" | "red";
	icon: string;
} {
	const statusMap: Record<
		string,
		{ label: string; color: "green" | "yellow" | "red"; icon: string }
	> = {
		connected: {
			label: "Connected",
			color: "green" as const,
			icon: "CheckCircle",
		},
		pending: { label: "Pending", color: "yellow" as const, icon: "Clock" },
		error: { label: "Error", color: "red" as const, icon: "XCircle" },
	};
	return statusMap[status] || statusMap.pending;
}

/**
 * Format cache hit rate as percentage string
 */
export function formatCacheHitRate(rate: number): string {
	return `${rate.toFixed(1)}%`;
}

/**
 * Calculate estimated savings from stats
 */
export function calculateSavings(stats: RouteConfigStats): {
	tokensSaved: number;
	percentageSaved: number;
} {
	const total = stats.totalTokens + stats.savedTokens;
	const percentageSaved = total > 0 ? (stats.savedTokens / total) * 100 : 0;
	return {
		tokensSaved: stats.savedTokens,
		percentageSaved: Math.round(percentageSaved * 10) / 10,
	};
}
